import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getObrasResumo, ObraResumo } from '../services/api';
import ObraCreateModal from '../components/ObraCreateModal';
import { formatDate, getDeadlineInfo } from '../utils/dateUtils';
import { capitalizeFirstLetter } from '../utils/formatters';
import {
    Container,
    Typography,
    CircularProgress,
    Alert,
    Box,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    LinearProgress,
    TextField,
    InputAdornment,
    Button,
    Paper,
    Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(value || 0);
};

const getStatusChipColor = (status: string): 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
        case 'Em Andamento':
            return 'primary';
        case 'Concluída':
        case 'Finalizada':
            return 'success';
        case 'Pausada':
            return 'warning';
        case 'Planejada':
            return 'info';
        default:
            return 'default';
    }
};

type SortOption = 'nome_asc' | 'custo_desc' | 'orcamento_pct_desc' | 'prazo_asc';

const ObrasListPage: React.FC = () => {
    const { data: obras = [], isLoading: loading, error: queryError, refetch } = useQuery<ObraResumo[]>({
        queryKey: ['obras-resumo'],
        queryFn: getObrasResumo,
    });

    const error = queryError ? 'Erro ao buscar dados das obras.' : null;
    const [isCreateModalOpen, setCreateModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('Todas');
    const [municipioFilter, setMunicipioFilter] = useState('Todos');
    const [responsavelFilter, setResponsavelFilter] = useState('Todos');
    const [sortBy, setSortBy] = useState<SortOption>('nome_asc');
    const navigate = useNavigate();

    const handleCardClick = (id: number) => {
        navigate(`/obras/${id}`);
    };

    const statusOptions = useMemo(() => {
        const base = ['Todas', 'Em Andamento', 'Planejada', 'Pausada', 'Concluída'];
        const fromData = obras.map(o => o.status).filter(Boolean);
        return Array.from(new Set([...base, ...fromData]));
    }, [obras]);

    const municipioOptions = useMemo(() => {
        const municipios = obras
            .map(o => o.municipio?.trim())
            .filter((m): m is string => Boolean(m));
        return ['Todos', ...Array.from(new Set(municipios)).sort((a, b) => a.localeCompare(b, 'pt-BR'))];
    }, [obras]);

    const responsavelOptions = useMemo(() => {
        const nomes = obras
            .flatMap(o => (o.responsaveis || []).map(r => r.nome?.trim()))
            .filter((n): n is string => Boolean(n));
        return ['Todos', ...Array.from(new Set(nomes)).sort((a, b) => a.localeCompare(b, 'pt-BR'))];
    }, [obras]);

    const filteredObras = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();

        const filtered = obras.filter((obra) => {
            if (statusFilter !== 'Todas' && obra.status !== statusFilter) {
                return false;
            }
            if (municipioFilter !== 'Todos' && (obra.municipio || '').trim() !== municipioFilter) {
                return false;
            }
            if (
                responsavelFilter !== 'Todos' &&
                !(obra.responsaveis || []).some(r => (r.nome || '').trim() === responsavelFilter)
            ) {
                return false;
            }
            if (query) {
                const matchNome = (obra.nome || '').toLowerCase().includes(query);
                const matchEndereco = (obra.endereco || '').toLowerCase().includes(query);
                const matchMunicipio = (obra.municipio || '').toLowerCase().includes(query);
                const matchResponsavel = (obra.responsaveis || []).some(r =>
                    (r.nome || '').toLowerCase().includes(query)
                );
                if (!matchNome && !matchEndereco && !matchMunicipio && !matchResponsavel) {
                    return false;
                }
            }
            return true;
        });

        return [...filtered].sort((a, b) => {
            switch (sortBy) {
                case 'custo_desc':
                    return (b.custo_total || 0) - (a.custo_total || 0);
                case 'orcamento_pct_desc': {
                    const pctA = a.orcamento_inicial > 0 ? (a.custo_total / a.orcamento_inicial) * 100 : 0;
                    const pctB = b.orcamento_inicial > 0 ? (b.custo_total / b.orcamento_inicial) * 100 : 0;
                    return pctB - pctA;
                }
                case 'prazo_asc': {
                    const dateA = a.data_termino_prevista ? new Date(a.data_termino_prevista).getTime() : Number.MAX_SAFE_INTEGER;
                    const dateB = b.data_termino_prevista ? new Date(b.data_termino_prevista).getTime() : Number.MAX_SAFE_INTEGER;
                    return dateA - dateB;
                }
                case 'nome_asc':
                default:
                    return (a.nome || '').localeCompare(b.nome || '', 'pt-BR');
            }
        });
    }, [obras, searchTerm, statusFilter, municipioFilter, responsavelFilter, sortBy]);

    if (error) {
        return <Alert severity="error" sx={{ margin: 2 }}>{error}</Alert>;
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ my: 4 }}>
                {/* Cabeçalho */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <Box>
                        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
                            Minhas Obras
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Gerencie o andamento, orçamento e prazos dos empreendimentos
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateModalOpen(true)}
                        sx={{ fontWeight: 600, px: 2.5, py: 1, borderRadius: 2 }}
                    >
                        Nova Obra
                    </Button>
                </Box>

                {/* Barra de Busca, Filtros e Ordenação */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 3,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        alignItems: 'center',
                    }}
                >
                    <TextField
                        size="small"
                        placeholder="Buscar por nome, endereço ou responsável..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ flexGrow: 1, minWidth: { xs: '100%', md: 240 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Status"
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            {statusOptions.map((status) => (
                                <MenuItem key={status} value={status}>
                                    {status}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 170 } }}>
                        <InputLabel>Município</InputLabel>
                        <Select
                            value={municipioFilter}
                            label="Município"
                            onChange={(e) => setMunicipioFilter(e.target.value)}
                        >
                            {municipioOptions.map((municipio) => (
                                <MenuItem key={municipio} value={municipio}>
                                    {municipio}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {responsavelOptions.length > 1 && (
                        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
                            <InputLabel>Responsável</InputLabel>
                            <Select
                                value={responsavelFilter}
                                label="Responsável"
                                onChange={(e) => setResponsavelFilter(e.target.value)}
                            >
                                {responsavelOptions.map((resp) => (
                                    <MenuItem key={resp} value={resp}>
                                        {resp}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 210 } }}>
                        <InputLabel>Ordenar por</InputLabel>
                        <Select
                            value={sortBy}
                            label="Ordenar por"
                            onChange={(e) => setSortBy(e.target.value as SortOption)}
                        >
                            <MenuItem value="nome_asc">Nome (A-Z)</MenuItem>
                            <MenuItem value="custo_desc">Maior Custo</MenuItem>
                            <MenuItem value="orcamento_pct_desc">Maior % do Orçamento</MenuItem>
                            <MenuItem value="prazo_asc">Prazo de Término</MenuItem>
                        </Select>
                    </FormControl>
                </Paper>

                {/* Listagem de Cards */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : filteredObras.length === 0 ? (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            textAlign: 'center',
                            borderRadius: 2,
                            border: '1px dashed',
                            borderColor: 'divider',
                        }}
                    >
                        <Typography variant="subtitle1" color="text.secondary">
                            {obras.length === 0
                                ? 'Nenhuma obra encontrada. Clique em "Nova Obra" para começar.'
                                : 'Nenhuma obra corresponde aos filtros selecionados.'}
                        </Typography>
                    </Paper>
                ) : (
                    <Box
                        display="grid"
                        gridTemplateColumns="repeat(auto-fill, minmax(320px, 1fr))"
                        gap={3}
                    >
                        {filteredObras.map((obra) => {
                            const orcamento = obra.orcamento_inicial || 0;
                            const custo = obra.custo_total || 0;
                            const saldo = orcamento - custo;
                            const pctOrcamento = orcamento > 0 ? (custo / orcamento) * 100 : 0;
                            const isCompleted = obra.status === 'Concluída' || obra.status === 'Finalizada';
                            const deadline = getDeadlineInfo(obra.data_termino_prevista, obra.status);

                            const progressColor: 'primary' | 'success' | 'warning' | 'error' =
                                pctOrcamento > 100
                                    ? 'error'
                                    : isCompleted
                                    ? 'success'
                                    : pctOrcamento >= 80
                                    ? 'warning'
                                    : 'primary';

                            const localizacao = [obra.municipio, obra.estado].filter(Boolean).join(' - ');
                            const tipoConstrucao = obra.tipo_de_construcao
                                ? capitalizeFirstLetter(obra.tipo_de_construcao)
                                : null;

                            return (
                                <Card
                                    key={obra.id}
                                    elevation={1}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        borderRadius: 2.5,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        transition: 'transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: 4,
                                        },
                                    }}
                                >
                                    <CardActionArea
                                        onClick={() => handleCardClick(obra.id)}
                                        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                                    >
                                        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2.5 }}>
                                            {/* Título e Status */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                                <Typography variant="h6" component="div" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                                                    {obra.nome}
                                                </Typography>
                                                <Chip
                                                    label={obra.status}
                                                    color={getStatusChipColor(obra.status)}
                                                    size="small"
                                                    sx={{ fontWeight: 600, flexShrink: 0 }}
                                                />
                                            </Box>

                                            {/* Metadados: Localização e Tipo / Unidades */}
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: obra.responsaveis && obra.responsaveis.length > 0 ? 1 : 2, color: 'text.secondary' }}>
                                                {localizacao && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <LocationOnOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {localizacao}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {(tipoConstrucao || (obra.quantidade_unidades && obra.quantidade_unidades > 0)) && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        {obra.tipo_de_construcao === 'apartamento' ? (
                                                            <ApartmentOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        ) : (
                                                            <HomeWorkOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        )}
                                                        <Typography variant="caption" color="text.secondary">
                                                            {[
                                                                tipoConstrucao,
                                                                obra.quantidade_unidades && obra.quantidade_unidades > 0
                                                                    ? `${obra.quantidade_unidades} ${obra.quantidade_unidades === 1 ? 'Unidade' : 'Unidades'}`
                                                                    : null,
                                                            ]
                                                                .filter(Boolean)
                                                                .join(' • ')}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>

                                            {obra.responsaveis && obra.responsaveis.length > 0 && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
                                                    <PeopleOutlineIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                        {obra.responsaveis.map(r => r.nome).join(', ')}
                                                    </Typography>
                                                </Box>
                                            )}

                                            <Divider sx={{ mb: 2 }} />

                                            {/* Bloco Financeiro */}
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    Custo Realizado
                                                </Typography>
                                                <Typography variant="h5" component="p" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                                                    {formatCurrency(custo)}
                                                </Typography>

                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Orçamento:
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {orcamento > 0 ? formatCurrency(orcamento) : 'Não definido'}
                                                    </Typography>
                                                </Box>

                                                {(obra.vgv_total || 0) > 0 && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            VGV Estimado:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                                            {formatCurrency(obra.vgv_total || 0)}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {orcamento > 0 && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {saldo < 0
                                                                ? 'Orçamento Excedido:'
                                                                : isCompleted
                                                                ? 'Economia:'
                                                                : 'Saldo Restante:'}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 700,
                                                                color: saldo < 0 ? 'error.main' : 'success.main',
                                                            }}
                                                        >
                                                            {formatCurrency(saldo)}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>

                                            {/* Barra de Progresso do Orçamento */}
                                            {orcamento > 0 && (
                                                <Box sx={{ mb: 2 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Consumo do Orçamento
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontWeight: 700,
                                                                color: pctOrcamento > 100 ? 'error.main' : 'text.secondary',
                                                            }}
                                                        >
                                                            {pctOrcamento.toFixed(1).replace('.', ',')}%
                                                        </Typography>
                                                    </Box>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={Math.min(pctOrcamento, 100)}
                                                        color={progressColor}
                                                        sx={{ height: 8, borderRadius: 4 }}
                                                    />
                                                </Box>
                                            )}

                                            {/* Rodapé: Prazo / Alerta */}
                                            <Box sx={{ mt: 'auto', pt: 1 }}>
                                                {deadline.isOverdue ? (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.75,
                                                            px: 1.25,
                                                            py: 0.75,
                                                            borderRadius: 1.5,
                                                            bgcolor: 'error.lighter',
                                                            backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                                            color: 'error.dark',
                                                        }}
                                                    >
                                                        <WarningAmberRoundedIcon sx={{ fontSize: 18, color: 'error.main' }} />
                                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'error.main' }}>
                                                            {deadline.label} ({formatDate(obra.data_termino_prevista)})
                                                        </Typography>
                                                    </Box>
                                                ) : isCompleted ? (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.75,
                                                            color: 'success.main',
                                                        }}
                                                    >
                                                        <CheckCircleOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                            Concluída • Término: {formatDate(obra.data_termino_prevista)}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.75,
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        <EventOutlinedIcon sx={{ fontSize: 17 }} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            Término Previsto: {formatDate(obra.data_termino_prevista)} ({deadline.label})
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            );
                        })}
                    </Box>
                )}
            </Box>

            <ObraCreateModal
                open={isCreateModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onObraAdded={() => {
                    setCreateModalOpen(false);
                    refetch();
                }}
            />
        </Container>
    );
};

export default ObrasListPage;
